import snakeCase from 'lodash.snakecase';

import { ensureConfig, getConfig, snakeCaseObject } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';

ensureConfig([
  'LMS_BASE_URL',
], 'Posts API service');

export const getThreadsApiUrl = () => `${getConfig().LMS_BASE_URL}/api/discussion/v1/threads/`;
export const getCoursesApiUrl = () => `${getConfig().LMS_BASE_URL}/api/discussion/v1/courses/`;

/**
 * Builds the requested_fields parameter conditionally including ban fields.
 * @param {boolean} enableDiscussionBan - Whether to include ban-related fields
 * @returns {string} Comma-separated list of fields
 */
const buildRequestedFields = (enableDiscussionBan = false) => {
  const fields = ['profile_image'];
  if (enableDiscussionBan) {
    fields.push('is_author_banned', 'author_ban_scope');
  }
  return fields.join(',');
};

/**
 * Fetches all the threads in the given course and topic.
 * @param {string} courseId
 * @param {string} author
 * @param {[string]} topicIds List of topics to limit threads to
 * @param {number} page
 * @param {number} pageSize
 * @param {string} textSearch A search string to match.
 * @param {ThreadOrdering} orderBy The results wil be sorted on this basis.
 * @param {boolean} following If true, only threads followed by the current user will be returned.
 * @param {boolean} flagged If true, only threads that have been reported will be returned.
 * @param {string} threadType Can be 'discussion' or 'question'.
 * @param {ThreadViewStatus} view Set to "unread" on "unanswered" to filter to only those statuses.
 * @param {boolean} countFlagged If true, abuseFlaggedCount will be available.
 * @param {number} cohort
 * @returns {Promise<{}>}
 */
export const getThreads = async (courseId, {
  topicIds,
  page,
  pageSize,
  textSearch,
  orderBy,
  following,
  view,
  author,
  flagged,
  threadType,
  countFlagged,
  cohort,
  isDeleted,
  enableDiscussionBan = false,
  includeMuted,
} = {}) => {
  const params = snakeCaseObject({
    courseId,
    page,
    pageSize,
    topicId: topicIds && topicIds.join(','),
    textSearch,
    threadType,
    orderBy: snakeCase(orderBy),
    following,
    view,
    requestedFields: buildRequestedFields(enableDiscussionBan),
    author,
    flagged,
    countFlagged,
    groupId: cohort,
    isDeleted,
    includeMuted,
  });
  const { data } = await getAuthenticatedHttpClient().get(getThreadsApiUrl(), { params });
  return data;
};

/**
 * Fetches a single thread.
 * @param {string} threadId
 * @param {string} courseId
 * @param {boolean} enableDiscussionBan
 * @returns {Promise<{}>}
 */
export const getThread = async (
  threadId,
  courseId,
  enableDiscussionBan = false,
  includeMuted = false,
) => {
  const params = {
    requested_fields: buildRequestedFields(enableDiscussionBan),
    course_id: courseId,
    include_muted: includeMuted,
  };

  const url = `${getThreadsApiUrl()}${threadId}/`;
  const { data } = await getAuthenticatedHttpClient().get(url, { params });
  return data;
};

/**
 * Posts a new thread.
 * @param {string} courseId
 * @param {string} topicId
 * @param {ThreadType} type The thread's type (either "question" or "discussion")
 * @param {string} title
 * @param {string} content
 * @param {number} cohort
 * @param {boolean} following Follow the thread after creating
 * @param {boolean} anonymous Should the thread be anonymous to all users
 * @param {boolean} anonymousToPeers Should the thread be anonymous to peers
 * @param notifyAllLearners
 * @param recaptchaToken
 * @param {boolean} enableInContextSidebar
 * @returns {Promise<{}>}
 */
export const postThread = async (
  courseId,
  topicId,
  type,
  title,
  content,
  {
    following,
    cohort,
    anonymous,
    anonymousToPeers,
    notifyAllLearners,
    recaptchaToken,
  } = {},
  enableInContextSidebar = false,
) => {
  const postData = snakeCaseObject({
    courseId,
    topicId,
    type,
    title,
    raw_body: content,
    following,
    anonymous,
    anonymousToPeers,
    groupId: cohort,
    enableInContextSidebar,
    notifyAllLearners,
    captchaToken: recaptchaToken,
  });
  const { data } = await getAuthenticatedHttpClient()
    .post(getThreadsApiUrl(), postData);
  return data;
};

/**
 * Updates an existing thread.
 * @param {string} threadId
 * @param {string} topicId
 * @param {ThreadType} type The thread's type (either "question" or "discussion")
 * @param {string} title
 * @param {string} content
 * @param {boolean} flagged
 * @param {boolean} voted
 * @param {boolean} read
 * @param {boolean} following
 * @param {boolean} closed
 * @param {boolean} pinned
 * @param {string} editReasonCode
 * @param {string} closeReasonCode
 * @returns {Promise<{}>}
 */
export const updateThread = async (threadId, {
  flagged,
  voted,
  read,
  topicId,
  type,
  title,
  content,
  following,
  closed,
  pinned,
  editReasonCode,
  closeReasonCode,
} = {}) => {
  const url = `${getThreadsApiUrl()}${threadId}/`;
  const patchData = snakeCaseObject({
    topicId,
    abuse_flagged: flagged,
    voted,
    read,
    type,
    title,
    raw_body: content,
    following,
    closed,
    pinned,
    editReasonCode,
    closeReasonCode,
  });
  const { data } = await getAuthenticatedHttpClient()
    .patch(url, patchData, { headers: { 'Content-Type': 'application/merge-patch+json' } });
  return data;
};

/**
 * Deletes a thread.
 * @param {string} threadId
 */
export const deleteThread = async (threadId) => {
  const url = `${getThreadsApiUrl()}${threadId}/`;
  await getAuthenticatedHttpClient()
    .delete(url);
};

/**
 * Upload a file.
 * @param {Blob} blob The file body
 * @param {string} filename
 * @param {string} courseId
 * @param {string} threadKey
 * @returns {Promise<{ location: string }>}
 */
export const uploadFile = async (blob, filename, courseId, threadKey) => {
  const uploadUrl = `${getCoursesApiUrl()}${courseId}/upload`;
  const formData = new FormData();
  formData.append('thread_key', threadKey);
  formData.append('uploaded_file', blob, filename);
  const { data } = await getAuthenticatedHttpClient().post(uploadUrl, formData);
  if (data.developer_message) {
    throw new Error(data.developer_message);
  }
  return data;
};

/**
 * Post send Account Activation Email.
 */
export const sendEmailForAccountActivation = async () => {
  const url = `${getConfig().LMS_BASE_URL}/api/send_account_activation_email`;
  const { data } = await getAuthenticatedHttpClient()
    .post(url);
  return data;
};

/**
 * Restore a deleted thread.
 * @param {string} threadId
 * @param {string} courseId
 * @returns {Promise<{}>}
 */
export const restoreThread = async (threadId, courseId) => {
  const url = `${getConfig().LMS_BASE_URL}/api/discussion/v1/restore_content`;
  const { data } = await getAuthenticatedHttpClient().post(url, {
    content_type: 'thread',
    content_id: threadId,
    course_id: courseId,
  });
  return data;
};
